import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toggleBodyClass } from '../../../utils/jsUtils';
import { noop } from '../../../utils/noop';
import WithOutsideClick from '../../../utils/WithOutsideClick';
import Loader from '../../Loader/Loader';
import BackgroundOverlay from '../BackgroundOverlay/BackgroundOverlay';
import { DrawerGlobalStyles, DrawerStyles } from './Drawer.style';

const Drawer = ({
  heading,
  icon,
  subHeading,
  children,
  toggleState,
  isLoading,
  onOpen,
  type,
  height,
  disableOutsideClick = false,
  disableDrawerClose = false,
  hideDivider = false,
  autoHeight = false,
  hideCloseButton = false,
  enableAdobeCarousel = false,
  isSizeChart = false,
}) => {
  const [isDrawerActive, activateDrawer] = toggleState;
  const [isMobile, setIsMobile] = useState(false);

  // Detect if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    toggleBodyClass(isDrawerActive, 'drawer-active');
  }, [isDrawerActive]);

  const drawerHeight = useMemo(() => {
    if (autoHeight) return 'auto';
    return height ? `${height}` : `90%`;
  }, [height, autoHeight]);

  useEffect(() => {
    if (isDrawerActive) onOpen();
  }, [isDrawerActive, onOpen]);

  const handleClose = () => {
    if (!disableDrawerClose) {
      activateDrawer(false);
    }
  };

  const handleOutsideClick = () => {
    if (!disableOutsideClick) {
      activateDrawer(false);
    }
  };

  const renderHeader = () => {
    if (isSizeChart) {
      return (
        <div className="drawer-header size-chart-header">
          <div className="size-chart-title">
            <i className="ruler-icon" />
            <span>{heading}</span>
          </div>
          {!hideCloseButton && (
            <i
              className="csicon-danger close-button"
              onKeyDown={noop}
              role="button"
              tabIndex={0}
              onClick={handleClose}
            />
          )}
        </div>
      );
    }
    return (
      <div className={`drawer-header ${type}`}>
        <div className="drawer-line" onClick={handleClose} onKeyDown={noop} role="button" tabIndex={0} />
        {heading && (
          <>
            <div className="title">
              <div className={`${icon}`} />
              <div>{heading}</div>
            </div>

            {subHeading && <div className="sub-heading">{subHeading}</div>}
            {!hideDivider && <div className="divider" />}
          </>
        )}
        {!hideCloseButton && (
          <i className="csicon-danger" onKeyDown={noop} role="button" tabIndex={0} onClick={handleClose} />
        )}
      </div>
    );
  };

  // Decide what type of drawer to use
  const effectiveType = isSizeChart && isMobile ? 'secondary' : type;
  const effectiveAutoHeight = isSizeChart && isMobile ? true : autoHeight;

  if (!isDrawerActive) {
    return null;
  }

  return createPortal(
    <div>
      <BackgroundOverlay />
      <div
        className={cx(
          `drawer-container ${effectiveType}`,
          isDrawerActive && 'is-open',
          effectiveAutoHeight && 'auto-height',
          isSizeChart && 'size-chart-drawer'
        )}
        style={
          isDrawerActive && !effectiveAutoHeight
            ? {
                transform: `translateY(calc(100% - ${drawerHeight}))`,
              }
            : {}
        }
      >
        <div className="drawer-layout">
          <WithOutsideClick onOutsideClick={handleOutsideClick}>
            {renderHeader()}

            {enableAdobeCarousel && (
              <div
                className="modal-body"
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}
              >
                <div className="header">Producto agregado a tu Carro</div>
              </div>
            )}

            <div className={`drawer-content ${effectiveType}`}>
              {children}
              {isLoading && <Loader />}
            </div>
          </WithOutsideClick>
        </div>
        <style jsx>{DrawerStyles}</style>
        <style jsx>{DrawerGlobalStyles}</style>
      </div>
    </div>,
    document.body
  );
};

Drawer.defaultProps = {
  subHeading: null,
  isLoading: true,
  onOpen: noop,
  type: '',
  icon: '',
  height: null,
  disableOutsideClick: false,
  disableDrawerClose: false,
  hideDivider: false,
  autoHeight: false,
  hideCloseButton: false,
  enableAdobeCarousel: false,
  isSizeChart: false,
};

Drawer.propTypes = {
  children: PropTypes.node.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string,
  toggleState: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func,
  type: PropTypes.string,
  icon: PropTypes.string,
  height: PropTypes.string,
  disableOutsideClick: PropTypes.bool,
  disableDrawerClose: PropTypes.bool,
  hideDivider: PropTypes.bool,
  autoHeight: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
  enableAdobeCarousel: PropTypes.bool,
  isSizeChart: PropTypes.bool,
};

export default Drawer;
